import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Home, ArrowRight, MapPin } from 'lucide-react';
import { sowService } from '../services/sowService';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';

interface PublicLandingPageProps {}

export const PublicLandingPage: React.FC<PublicLandingPageProps> = () => {
  const { alias } = useParams<{ alias: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState(false);

  const {
    ready,
    value: addressValue,
    suggestions: { status, data },
    setValue: setAddressValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressSelected = async (address: string) => {
    setAddressValue(address, false);
    clearSuggestions();

    try {
      const geocodeResults = await getGeocode({ address });
      if (geocodeResults.length > 0) {
        const addressComponents = geocodeResults[0].address_components;

        const streetNumber = addressComponents.find((c: any) => c.types.includes('street_number'))?.long_name;
        const route = addressComponents.find((c: any) => c.types.includes('route'))?.long_name;
        const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name;
        const state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name;
        const zip = addressComponents.find((c: any) => c.types.includes('postal_code'))?.long_name;

        // Build complete address with zip
        const line1 = `${streetNumber || ''} ${route || ''}`.trim();
        const completeAddress = `${line1}, ${city || ''}, ${state || ''} ${zip || ''}`.trim();

        setFormData(prev => ({
          ...prev,
          address: completeAddress
        }));
      } else {
        // Fallback to original address
        setFormData(prev => ({
          ...prev,
          address: address
        }));
      }
    } catch (error) {
      // Fallback to original address if geocoding fails
      setFormData(prev => ({
        ...prev,
        address: address
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      setError('Please fill in all fields');
      return;
    }

    if (!alias) {
      setError('Invalid link');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await sowService.createPublicSOW(alias, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
      });

      // Redirect to public wizard to collect scope definition
      navigate(`/scope/${result.sowId}/wizard`);
    } catch (error: any) {
      console.error('Error creating project:', error);
      setError(error.message || 'Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl shadow-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
              <Home className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">Create Your Renovation Project</CardTitle>
          <CardDescription className="text-lg mt-2">
            Get started with a detailed project scope and questionnaire
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="text-sm font-medium">
                Full Name *
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Smith"
                required
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="email" className="text-sm font-medium">
                Email Address *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@example.com"
                required
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="phone" className="text-sm font-medium">
                Phone Number *
              </label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                required
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="address" className="text-sm font-medium">
                Project Address *
              </label>

              {!manualEntry ? (
                <>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10" />
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={addressValue}
                      onChange={(e) => setAddressValue(e.target.value)}
                      placeholder="Start typing an address..."
                      required
                      disabled={!ready}
                      className="pl-10"
                    />
                  </div>

                  {/* Address Suggestions */}
                  {status === 'OK' && (
                    <div className="mt-2 border rounded-md shadow-sm overflow-hidden bg-white">
                      {data.map((suggestion: any) => (
                        <div
                          key={suggestion.place_id}
                          onClick={() => handleAddressSelected(suggestion.description)}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="flex items-start">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-sm">{suggestion.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {formData.address && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">Selected: {formData.address}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setManualEntry(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Can't find your address? Enter manually
                  </button>
                </>
              ) : (
                <>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St, City, State 12345"
                    required
                    className="mt-1"
                  />
                  <button
                    type="button"
                    onClick={() => setManualEntry(false)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Use address search instead
                  </button>
                </>
              )}
            </div>

            {error && (
              <div className="bg-red-50 text-red-800 p-4 rounded border border-red-200">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Creating Your Project...
                </>
              ) : (
                <>
                  Create Your Project
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>What happens next?</strong> After creating your project, you'll answer a detailed questionnaire to help us understand your renovation needs. This information will be used to create an accurate project scope and estimate.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicLandingPage;
